import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { SolIcon } from '@sol/icons';
import { DetailService } from '../../../services/detail.service';
import { TimerTools } from '../../../tools/timer-tools';
import { Subscription } from 'rxjs';
import { ShortcutService, SolShortcut, SolShortcutSubscription } from '../../../services/shortcut.service';
import { AbstractDetailComponent } from '../abstract-detail-component.directive';
import { SolButtonType } from '../../../models/core/sol-button';
import { DetailStatus, SolDetailsState } from './sol-details.state';

@Component({
  selector: 'sol-details',
  imports: [],
  templateUrl: './sol-details.component.html',
})
export class SolDetailsComponent extends AbstractComponent<SolDetailsState> implements AfterViewInit, OnDestroy {
  protected readonly SolIcon = SolIcon;
  protected readonly SolButtonType = SolButtonType;

  // Dependencies
  private readonly detailService = inject(DetailService);
  private readonly shortcutService = inject(ShortcutService);

  // Computed
  protected readonly visible$ = this.compute(state => state.visible);
  protected readonly closing$ = this.compute(state => state.closing);
  protected readonly fixed$ = this.compute(state => state.fixed);
  protected readonly embedded$ = this.compute(state => state.embedded);

  /**
   * True when the app runs inside the Vue host shell. The host already fixes its
   * own navbar above us, so a second bar holding its place would eat the
   * viewport — embedded lets the topbar scroll away with the content.
   */
  @Input()
  public set embedded(embedded: boolean) {
    this.update(state => state.withEmbedded(embedded));
  }

  // Binding
  @ViewChild('detail', { read: ViewContainerRef })
  private detailDiv?: ViewContainerRef;
  @ViewChild('detailContainer')
  private detailContainerRef!: ElementRef<HTMLElement>;

  // Cache
  private closingSubscription?: Subscription;
  private shortcutSubscription?: SolShortcutSubscription;
  private detailRef?: ComponentRef<any>;

  constructor() {
    super(SolDetailsState.of());

    this.detailService.watch(detailState => {
      const hasDetail = !!detailState.detail;
      const status = hasDetail ? DetailStatus.open : DetailStatus.closing;
      const mode = detailState.detail?.mode ?? 'default';
      const effectiveFixed = mode === 'fixed' ? true : mode === 'floating' ? false : detailState.fixed;
      this.update(state => state.withDetailUpdate(detailState.detail, effectiveFixed, detailState.width, status));

      TimerTools.nextTick(() => this.updateDetailComponent());

      if (status === DetailStatus.closing) {
        this.closingSubscription = TimerTools.timer$(350, this.destroyRef).subscribe(() => {
          this.update(state => state.withStatus(DetailStatus.closed));
          this.updateDetailComponent();
          this.closingSubscription = undefined;
        });
      } else if (hasDetail) {
        this.closingSubscription?.unsubscribe();
        this.closingSubscription = undefined;
      }
    });
  }

  @HostListener('mouseup')
  onMouseUp(): void {
    if (this.state.dragX < 0) {
      return;
    }
    const dragElement = this.detailRef?.location.nativeElement as HTMLElement;
    const dragWidth = dragElement.offsetWidth;
    this.update(state => state.withDragEnd(dragWidth));
    this.detailService.setWidth(dragWidth);
  }

  ngAfterViewInit(): void {
    this.shortcutSubscription = this.shortcutService.register('sol-details', false, this.detailContainerRef, [
      {
        shortcut: SolShortcut.of('Escape'),
        action: () => this.onShortcutEscape(),
      },
    ]);
  }

  ngOnDestroy(): void {
    this.detailDiv?.clear();
    this.detailRef = undefined;
    this.shortcutSubscription?.unsubscribe();
    this.closingSubscription?.unsubscribe();
  }

  onDragStart(event: MouseEvent) {
    // Consume event
    event.preventDefault();
    event.stopPropagation();

    // Get client x position & store current detailDiv width
    const dragX = event.clientX;
    const dragElement = this.detailRef?.location.nativeElement as HTMLElement;
    const dragWidth = dragElement.offsetWidth;
    this.update(state => state.withDragStart(dragX, dragWidth));
  }

  onDragMove(event: MouseEvent) {
    // Consume event
    event.preventDefault();
    event.stopPropagation();

    if (!this.detailDiv) {
      return;
    }

    // Get client x position
    const clientX = event.clientX;

    // Calculate new width
    const state = this.state;
    if (state.dragX < 0 || state.dragWidth <= 0) {
      return;
    }
    const diff = state.dragX - clientX;
    const newWidth = Math.max(DetailService.minWidth, state.dragWidth + diff);
    this.updateDetailWidth(newWidth);
  }

  private onShortcutEscape(): boolean {
    if (this.state.status === DetailStatus.open && this.state.detail?.closeable !== false) {
      this.detailService.close();
      return true;
    }
    return false;
  }

  private updateDetailWidth(newWidth: number) {
    const dragElement = this.detailRef?.location.nativeElement as HTMLElement;
    dragElement.style.width = `${newWidth}px`;
  }

  private updateDetailComponent() {
    if (!this.detailDiv) {
      return;
    }

    // Closing?
    const state = this.state;
    if (state.status === DetailStatus.closing) {
      return;
    }

    // Clean up detail
    const detail = state.detail;
    const currentType = state.currentType;
    const currentId = state.currentId;
    const typeChanged = currentType !== detail?.componentType;
    const idChanged = currentId !== detail?.detailId;

    if (!detail || typeChanged || idChanged) {
      this.detailRef = undefined;
      this.detailDiv?.clear();
    }

    // Nothing to show
    if (!detail) {
      this.update(state => state.withCurrentDetail(undefined, undefined));
      return;
    }

    // Update existing or create new
    if (this.detailRef) {
      this.detailRef.instance.onAction = (actionId: string) => detail.onAction(actionId);
      detail.configurer(this.detailRef);
    } else {
      this.detailRef = this.detailDiv.createComponent(detail.componentType);
      const instance = this.detailRef.instance as AbstractDetailComponent<any>;
      instance.onAction = (actionId: string) => detail.onAction(actionId);
      detail.configurer(this.detailRef);
      const detailElement = this.detailRef.location.nativeElement as HTMLElement;
      detailElement.classList.add('sol-detail-impl');
      this.updateDetailWidth(state.dragWidth);
      this.update(state => state.withCurrentDetail(detail.componentType, detail.detailId));
    }
  }
}
